import InfoIcon from '@mui/icons-material/Info';
import { Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ProductTenor } from '@protos/product';
import { Side } from '@protos/trading';
import { OtcOrderType } from '@protos/v2/otcQuote';
import theme from '@shared/themes/darkTheme';
import { TradingPanel } from '../TradingContext';

interface TradeConfirmationProps {
  otcSide: Side;
  tradingForm: TradingPanel;
  availableTradingTenors: ProductTenor[];
  prices: { buy?: { price: string; amount: string }; sell?: { price: string; amount: string } };
  onClose: () => void;
  onConfirm: () => void;
  lockedBuyPrice?: string;
  lockedSellPrice?: string;
  orderType?: OtcOrderType;
  limitBuyPrice?: number;
  limitSellPrice?: number;
}

export const TradeConfirmation = ({
  otcSide,
  tradingForm,
  availableTradingTenors,
  prices,
  onClose,
  onConfirm,
  lockedBuyPrice,
  lockedSellPrice,
  orderType,
  limitBuyPrice,
  limitSellPrice,
}: TradeConfirmationProps) => {
  const isDisabled =
    Boolean(otcSide === Side.SELL && lockedSellPrice && Number(lockedSellPrice) > Number(prices.sell?.price || 0)) ||
    Boolean(otcSide === Side.BUY && lockedBuyPrice && Number(lockedBuyPrice) < Number(prices.buy?.price || 0));
  const lockedPrice = otcSide === Side.BUY ? lockedBuyPrice : lockedSellPrice;

  let priceToDisplay = prices[otcSide]?.price;

  if (orderType === OtcOrderType.limit && limitBuyPrice !== undefined && limitSellPrice !== undefined) {
    priceToDisplay = otcSide === Side.BUY ? limitBuyPrice.toFixed(2) : limitSellPrice.toFixed(2);
  }

  return (
    <Stack
      display="flex"
      flexDirection="column"
      flex={1}
      alignItems="center"
      alignSelf="center"
      justifyContent="space-between"
      onKeyUp={() => {
        console.log('key');
      }}
      sx={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: 2,
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '250px',
        width: '100%',
        padding: 1,
      }}
    >
      <Tooltip
        title={
          <span>
            You can enable/disable trade confirmations in the{' '}
            <Link href="/manage-account" color="primary" underline="hover">
              Manage Account
            </Link>{' '}
            page
          </span>
        }
        placement="right"
        arrow
      >
        <IconButton
          size="small"
          disableRipple
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            color: 'text.secondary',
            fontSize: 12,
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Stack alignItems="center" spacing={-1}>
        <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.text.primary} sx={{ fontSize: 10 }}>
          YOU ARE PLACING A {orderType === OtcOrderType.limit ? 'LIMIT' : ''}
        </Typography>
        <Typography fontSize={10} fontWeight="bold" color={otcSide === Side.BUY ? theme.palette.background.buy : theme.palette.background.sell}>
          {otcSide?.toUpperCase()} ORDER
        </Typography>
      </Stack>

      <Stack spacing={0.25} sx={{ width: '100%', flex: 1, py: 0.25 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={10} fontWeight="bold" color="textSecondary">
            Symbol:
          </Typography>
          <Typography fontSize={10} fontWeight="bold">
            {tradingForm.selectedProduct.toLocaleUpperCase()}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={10} fontWeight="bold" color="textSecondary">
            Tenor:
          </Typography>
          <Typography fontSize={10} fontWeight="bold" sx={{ textAlign: 'right', maxWidth: '50%' }}>
            {typeof tradingForm?.selectedTenor === 'object'
              ? (() => {
                  const leftTenor = availableTradingTenors.find(
                    t => typeof tradingForm.selectedTenor === 'object' && t.code === tradingForm.selectedTenor?.left
                  );
                  const rightTenor = availableTradingTenors.find(
                    t => typeof tradingForm.selectedTenor === 'object' && t.code === tradingForm.selectedTenor?.right
                  );
                  return `${leftTenor?.display?.toUpperCase() || tradingForm.selectedTenor?.left} - ${
                    rightTenor?.display?.toUpperCase() || tradingForm.selectedTenor?.right
                  }`;
                })()
              : (() => {
                  const tenor = availableTradingTenors.find(t => t.code === tradingForm.selectedTenor);
                  return tenor?.display?.toUpperCase() || tradingForm.selectedTenor;
                })()}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={10} fontWeight="bold" color="textSecondary">
            Lots:
          </Typography>
          <Typography fontSize={10} fontWeight="bold">
            {tradingForm.tradeSize}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={10} fontWeight="bold" color="textSecondary">
            {lockedPrice ? 'Price (locked at ' + lockedPrice + '):' : 'Price:'}
          </Typography>
          <Typography fontSize={10} fontWeight="bold" color={otcSide === Side.BUY ? theme.palette.background.buy : theme.palette.background.sell}>
            {priceToDisplay}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={10} fontWeight="bold" color="textSecondary">
            Exchange:
          </Typography>
          <Typography fontSize={10} fontWeight="bold">
            {tradingForm.selectedExchange.toLocaleUpperCase()}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 0.25,
            mt: 0.1,
          }}
        ></Stack>
      </Stack>

      <Stack direction="row" gap={0.5} spacing={0.5}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: '50px',
            fontSize: 10,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.lighter + ' !important',
            '&:hover': {
              backgroundColor: theme.palette.background.lightHover + ' !important',
              color: theme.palette.text.dark,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isDisabled}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: '50px',
            fontSize: 10,
            background: otcSide === Side.BUY ? theme.palette.background.buy : theme.palette.background.sell,
            '&:hover': {
              backgroundColor:
                otcSide === Side.BUY ? `${theme.palette.background.buyHover} !important` : `${theme.palette.background.sellHover} !important`,
            },
            transition: 'all 0.2s ease',
          }}
          autoFocus={true}
        >
          Confirm
        </Button>
      </Stack>
    </Stack>
  );
};
